import React, { useMemo, useCallback, useState, ReactNode, useEffect, } from 'react'

import {
  Row,
  Menu,
  Dropdown,
  Button,
  Space,
  Tooltip, Select, Col
} from 'antd'

import {
  DownOutlined,
  VerticalAlignBottomOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  SettingOutlined,
  UngroupOutlined,
  GroupOutlined
} from '@ant-design/icons'

import colorMaps from '../../hooks/useVTK/colorMaps'

import { COLORSET_REGEXP, DEFAULT_COLORSET, VIEW } from '../../hooks'

import {vec3} from 'gl-matrix'

import { VTKDataRangeInput } from '../VTKDataRangeInput'
import { InputLabel } from '../InputLabel/InputLabel'

import './VTKViewDropdown.css'

type DataSetGroup = {
  name: string, 
  colorBy: string, 
  id: string, 
  data: DataSet[] 
}

type DataSet = {
  name: string, 
  range: [number, number]
}
interface VTKViewDropdownProps {
  dispatch?: any
  viewerState?: any
  handleScreenshot?: () => void
  inline?: boolean
  showDataOptions?: boolean
}

const views = [
  {
    label: 'Top',
    key: 'top',
    icon: <VerticalAlignBottomOutlined />
  },
  {
    label: 'Bottom',
    key: 'bottom',
    icon: <VerticalAlignBottomOutlined rotate={180} />
  },
  {
    label: 'Left',
    key: 'left',
    icon: <VerticalAlignBottomOutlined rotate={90} />
  },
  {
    label: 'Right',
    key: 'right',
    icon: <VerticalAlignBottomOutlined rotate={270} />
  },
  {
    label: 'Front',
    key: 'front',
    icon: <CaretUpOutlined />
  },
  {
    label: 'Back',
    key: 'back',
    icon: <CaretDownOutlined />
  },
]

export const VTKViewDropdown: React.FunctionComponent<VTKViewDropdownProps> = ({ dispatch, viewerState, handleScreenshot, inline = false, showDataOptions = true }) => {

  const [selDataSet, setSelDataSet] = useState<{value: string, label: ReactNode}>()

  const selectedView = useMemo(() => {
    if (!viewerState || !viewerState.renderer || !viewerState.renderer.camera) return undefined
    const { directionOfProjection, viewUp } = viewerState.renderer.camera

    const cameraDirection = vec3.create()
    const [dx, dy, dz] = directionOfProjection
    const dP = vec3.fromValues(dx, dy, dz)
    vec3.scale(cameraDirection, dP, -1)

    const [cux, cuy, cuz] = viewUp
    const cameraUp = vec3.fromValues(cux, cuy, cuz)
    
    for (const view of Object.keys(VIEW)) {
      const [cx, cy, cz] = VIEW[view].direction
      const viewDirection = vec3.fromValues(cx, cy, cz)
      const [ux, uy, uz] = VIEW[view].up
      const up = vec3.fromValues(ux, uy, uz)
      if (vec3.equals(cameraDirection, viewDirection) && vec3.equals(cameraUp, up)) return view
    }
    return undefined
  }, [viewerState])

  const dataSets = useMemo(() => {
    if (!viewerState || !viewerState.scene || !viewerState.scene.length) return []
    return viewerState.scene.reduce((p, item) => {
      const mapper  = item.state.mapper
      const inputData = mapper.inputData
      const data = [...inputData.cellData.arrays, ...inputData.pointData.arrays]
      if ( data.length === 0 ) return p
      return [...p, {name: item.name, id: item.id , colorBy: mapper.colorByArrayName, data}]
    }, [])
  }, [viewerState])

  const colorSet = useMemo(() => {
    if (!viewerState || !viewerState.scene || !viewerState.scene.length) return DEFAULT_COLORSET.toLowerCase().replace(COLORSET_REGEXP, '')
    const colors = viewerState.scene.reduce((p, item) => {
      const colorSet  = item.state.mapper.colorSet
      if (!colorSet) return p
      return [...p, colorSet.toLowerCase().replace(COLORSET_REGEXP, '')]
    }, [])
    return colors.length > 0 ? colors : DEFAULT_COLORSET.toLowerCase().replace(COLORSET_REGEXP, '')
  }, [viewerState])

  const dataIsolated = useMemo(() => {
    if (!viewerState || !viewerState.scene || !viewerState.scene.length) return false
    return viewerState.scene.reduce((p, item) => {
      const inputData  = item.state.mapper.inputData
      const data = [...inputData.cellData.arrays, ...inputData.pointData.arrays]
      const hasData = data.length > 0
      // if hasData and is visibible, true
      // if hasData and is not visibile, false
      // if not hasData and is visible, false
      // if not hasData and is not visibile, true
      return p && ((hasData && item.state.visibility) || (!hasData && !item.state.visibility))
    }, true)
  }, [viewerState])

  const selDataSetIds = useMemo(() => {
    if (!selDataSet) return undefined
    const id = selDataSet.value.split('::')
    if (id.length !== 2) return undefined
    return [id[1]]
  }, [selDataSet])

  const handleViewSelect = useCallback(({ key }: any) => {
    if (!dispatch) return
    dispatch({ type: 'select-view', value: key })
  },[dispatch])

  const handleIsolateData = useCallback((bool: boolean) => {
    if (!dispatch) return
    dispatch({ type: 'isolate-data', value: bool })
  }, [dispatch])

  return (
    <Dropdown overlay={
      <Menu style={{padding:'24px 16px', maxWidth:300}}>
        <Space direction='vertical'
          size={16}
        >
          <Row justify='space-between'>
            <InputLabel label='Select View'
              span={20}
            >
              <Dropdown overlay={
                <Menu onClick={handleViewSelect}
                  selectedKeys={[selectedView]}
                  style={{width:'100%', padding:4}}
                >
                  {
                    views.map((item) => (
                      <Menu.Item key={item.key}
                        icon={item.icon}
                        style={{margin:'2px 0px', borderRadius:4}}
                      >
                        {item.label}
                      </Menu.Item>
                    ))
                  }
                </Menu>
              }
              trigger={['click']}
              key='p-views-dropdown'
              >
                <Button style={{width:'100%'}}>
                  {selectedView ? selectedView.charAt(0).toUpperCase() + selectedView.slice(1) : 'Views'} 
                  <DownOutlined />
                </Button>
              </Dropdown>
            </InputLabel>
            <Col span={4}
              style={{textAlign:'end'}}
            >
              <Tooltip title={dataIsolated ? 'Show All' : 'Isolate Data'}>
                <Button shape='circle'
                  onClick={() => handleIsolateData(dataIsolated)}
                  style={{ zIndex: 1 }}
                >
                  {dataIsolated ? <GroupOutlined/> : <UngroupOutlined/> }
                </Button>
              </Tooltip>
            </Col>
          </Row>
          { showDataOptions &&
            <>
              <Row justify='space-between'>
                <InputLabel label='Configure Selected Dataset'
                  span={24}
                >
                  <Select 
                    placeholder='Dataset...'
                    value={selDataSet}
                    labelInValue={true}
                    style={{width:'100%'}} 
                    dropdownStyle={{padding: '2px 4px'}}
                    dropdownMatchSelectWidth
                    onChange={({ value }, option) => {
                      const [colorBy, id] = value.split('::')
                      dispatch({ type: 'color-by', ids: [id], value: colorBy })
                      setSelDataSet({ value, label: colorBy })
                    }}
                  >
                    { dataSets.map(( dataSet: DataSetGroup) =>
                      <Select.OptGroup 
                        label={dataSet.name} 
                        key={`${dataSet.name.toLocaleLowerCase().replace(' ', '-')}::${dataSet.id}`}
                      >
                        { dataSet.data.map((data: DataSet) => 
                          <Select.Option 
                            value={`${data.name}::${dataSet.id}`}
                            key={`${data.name.toLocaleLowerCase().replace(' ', '-')}::${dataSet.id}`} 
                            style={{
                              margin: '2px 0px', 
                              borderRadius: 4
                            }}
                          >
                            {data.name}
                          </Select.Option>)}
                      </Select.OptGroup>
                    )}
                  </Select>
                </InputLabel>
              </Row>
              <InputLabel label='Choose colorset'>
                <Select value={colorSet}
                  onChange={(value: string) => {
                    dispatch({ type: 'color-set', ids: selDataSetIds, value })
                  }}
                  disabled={!selDataSetIds}
                  style={{ width: '100%' }}
                  dropdownStyle={{padding: '2px 4px'}}
                  dropdownMatchSelectWidth
                >
                  {colorMaps.rgbPresetNames.map((name: string, i) =>
                    <Select.Option value={name.toLowerCase().replace(COLORSET_REGEXP, '')}
                      key={`${name}-${i}`}
                      style={{margin:'2px 0px', borderRadius: 4}}
                    >{name}</Select.Option>
                  )}
                </Select>
              </InputLabel>
              <VTKDataRangeInput 
                state={viewerState} 
                dispatch={dispatch} 
                ids={selDataSetIds} 
                disabled={!selDataSetIds}
              />
            </>
          }
        </Space>
      </Menu>
    }
    trigger={['click']} 
    key='p-data-range-dropdown' 
    placement='bottom' 
    arrow
    >
      { inline ?
        <Button shape='circle'>
          <SettingOutlined/>
        </Button>
        :
        <Button shape='circle'
          className="p-float"
        >
          <SettingOutlined/>
        </Button>
      }
    </Dropdown>
  )
}